import classNames from "classnames";
import escapeHTML from "escape-html";
import { observer } from "mobx-react";
import React from "react";
import { useStore } from "../../models";
import { ToastMessageType } from "../../models/ui";
import styles from "./toastArea.css";

const escapeHtmlWithoutBoldAndBreakline = (textToEscape: string) => {
  const splitText = textToEscape.split(/<b>|<\/b>/);
  let escapedText = "";
  if (splitText.length > 1) {
    splitText.forEach((text, i) => {
      const boldTag = i === splitText.length - 1 ? "" : i % 2 ? "</b>" : "<b>";
      escapedText = escapedText + escapeHTML(text) + boldTag;
    });
  } else {
    escapedText = escapeHTML(splitText[0]!);
  }
  escapedText = escapedText.replace(/&lt;br&gt;/g, "<br>");
  return escapedText;
};

interface ToastProps {
  message: string;
  type: ToastMessageType;
  onDestruct: () => void;
}

interface ToastState {
  dismissing: boolean;
}

/**
 * Component representing a single toast
 */
class Toast extends React.Component<ToastProps, ToastState> {
  private destructTimer?: number;

  constructor(props: ToastProps) {
    super(props);
    this.state = { dismissing: false };
  }

  /**
   * Clear any existing timed calls to onDestruct
   */
  clearDestructTimer = () => this.destructTimer && clearTimeout(this.destructTimer);

  onDismiss = (ev: React.SyntheticEvent) => {
    this.clearDestructTimer(); // eg. if user manages to click dismiss multiple times
    // set dismissing state to enable playback of CSS anim and self-destruct after it's done
    this.setState({ dismissing: true });
    this.destructTimer = window.setTimeout(this.props.onDestruct, 400);
  };

  componentWillUnmount() {
    this.clearDestructTimer();
  }

  render() {
    const { message, type } = this.props;
    const escapedText = escapeHtmlWithoutBoldAndBreakline(message);
    const containerClassName = classNames(styles["toast-container"], {
      [styles["toast-dismissing"]]: this.state.dismissing
    });
    const toastClassName = classNames(styles.toast, {
      [styles.error]: type === ToastMessageType.DANGER
      // TODO: add more toast type styles (info, warning, etc)
    });

    return (
      <div className={containerClassName}>
        <div className={toastClassName}>
          <span className={styles.dismiss} onClick={this.onDismiss}>
            <i className="icon-close-cross" />
          </span>
          <div dangerouslySetInnerHTML={{ __html: escapedText }} />
        </div>
      </div>
    );
  }
}

export const ToastArea = observer(() => {
  const { uiStore } = useStore();

  return (
    <div className={styles.container}>
      {[...uiStore!.toastMessages].map(([key, toast]) => (
        <Toast
          key={key}
          message={toast.message}
          type={toast.type}
          onDestruct={() => uiStore!.removeToastMessage(key)}
        />
      ))}
    </div>
  );
});
